// FounderDecision widget — admin variant (H.3.4a).
//
// Copy-trim of apps/web-outreach/src/components/widgets/founder-decision.tsx
// with useOutreachChatContext → useAdminChatContext. Rendered inline when the
// admin gate (or AskUserQuestion) emits a
// `:::widget[founder-decision]{...}:::` directive for a generic yes/no
// confirmation (ScanInvariants / SeedEvent). The card shows the question + 2
// option buttons; a click POSTs through resumeFromDecision → /admin-agent/
// decision (Model Y). PD-4: this calls useAdminChatContext() UNCONDITIONALLY
// and throws without an AdminChatProvider.

import { useState } from 'react';
import { useAdminChatContext } from '../../lib/admin-chat-context';

interface Option {
  label: string;
  value: string;
}

// Props arrive as a flat record from the MarkdownRenderer (camelCased via
// ATTR_CAMELCASE_MAP). tool_use_id stays snake_case; sessionId round-trips
// from sessionid; options is a double-encoded JSON string parsed here.
interface Props {
  tool_use_id?: string;
  question?: string;
  options?: Option[] | string;
  sessionId?: string;
  decidedOverride?: string;
}

function parseOptions(input: Props['options']): Option[] {
  if (Array.isArray(input)) return input;
  if (typeof input !== 'string') return [];
  try {
    const parsed = JSON.parse(input);
    return Array.isArray(parsed) ? (parsed as Option[]) : [];
  } catch {
    return [];
  }
}

export function FounderDecision(p: Props) {
  const { tool_use_id, question, sessionId, decidedOverride } = p;
  const options = parseOptions(p.options);
  const [decided, setDecided] = useState<string | null>(decidedOverride ?? null);
  const [pending, setPending] = useState(false);
  const { resumeFromDecision } = useAdminChatContext();

  const click = async (opt: Option) => {
    if (pending || decided) return;
    setPending(true);
    try {
      await resumeFromDecision({
        sessionId: sessionId ?? '',
        tool_use_id: tool_use_id ?? '',
        action: opt.value,
      });
      setDecided(opt.label);
    } catch (e) {
      console.warn('[founder-decision] resume failed:', e);
    } finally {
      setPending(false);
    }
  };

  if (decided) {
    return (
      <div
        data-testid="founder-decision"
        data-state="decided"
        className="my-2 rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground-mute"
      >
        Chose: {decided}.
      </div>
    );
  }

  return (
    <div
      data-testid="founder-decision"
      data-state="idle"
      className="my-2 rounded-md border border-border bg-card p-3"
    >
      <div className="text-sm font-semibold text-foreground">{question}</div>
      <div className="mt-2 flex flex-wrap gap-2">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            data-testid={`fd-opt-${o.value}`}
            onClick={() => click(o)}
            disabled={pending}
            className="rounded-md bg-primary px-3 py-1 text-sm text-primary-foreground disabled:cursor-not-allowed disabled:opacity-40"
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
